// brain-browse.ts — the BROWSE pane of the Brain screen (#/brain), its
// landing view (design §8r: "The Bridge's admin surface BROWSES entities and
// their claims … the graph around an entity"). Before it the page reached an
// entity ONLY by typing its id, so the owner could not see what the brain
// holds without guessing ids.
//
// Reads GET /api/admin/brain/entities: a TYPE list with counts (from the
// landed schema, so a type with 0 entities still shows), a TAG filter, a
// search box (id / name / alias / claim values), and the entity table (the
// console's railed AG grid via makeGrid, the same lane users.ts/audit.ts
// mount), paged. Every id is a link to the entity page. The server decides;
// an empty brain renders its NAMED state and a refusal renders verbatim.
//
// The pure pieces (url, columns, type list, the state line) are exported so
// brain-browse.test.mjs can pin them without a DOM.

import type { ColDef, GridApi } from 'ag-grid-community';
import { el, jsonFetch, makeGrid, relativeTime } from './admin-ui.js';
import { mkIconBtn } from './buttons.js';
import { mkFcSelect, type FcOption, type FcSelectCtl } from './form-controls.js';
import { mkSearchBox } from './search.js';

export interface BrowseType { type: string; count: number; in_schema: boolean; origin: string | null; parent: string | null }
export interface BrowseRow { id: string; type: string; name: string | null; proposed_type: string | null; tags: string[]; claims: number; relations: number; invalidated: number; path: string; last_commit: string | null; last_changed: string | null }
export interface BrowseResponse {
  organisation: string; branch: string; state: 'ready' | 'empty'; reason?: string; line: string; sha: string | null;
  total: number; all: number; offset: number; limit: number; rows: BrowseRow[]; types: BrowseType[]; tags: Array<{ tag: string; count: number }>;
}
export interface BrowseState { type: string; tag: string; q: string; offset: number; limit: number }

export const BROWSE_PAGE = 50;

export function entityHref(id: string): string { return `#/brain/entity/${encodeURIComponent(id)}`; }
export function typeHref(type: string): string { return type ? `#/brain/type/${encodeURIComponent(type)}` : '#/brain'; }

/** A link to one entity's page — the graph is walked by clicking these. */
export function entityLink(id: string, text?: string): HTMLAnchorElement {
  const a = el('a', 'hc-brain-link', text ?? id);
  a.href = entityHref(id);
  return a;
}

/** The query string alone. The fetch site spells the path as an inline
 *  literal (`/api/admin/brain/entities?${browseQuery(st)}`) because the PAT
 *  ratchet's UI-callsite sweep (pat-coverage.test.mjs) extracts the path
 *  from the call's text — a URL built behind a helper is an un-extracted site. */
export function browseQuery(s: BrowseState): string {
  const p = new URLSearchParams();
  if (s.type) p.set('type', s.type);
  if (s.tag) p.set('tag', s.tag);
  if (s.q.trim()) p.set('q', s.q.trim());
  if (s.offset > 0) p.set('offset', String(s.offset));
  p.set('limit', String(s.limit));
  return p.toString();
}

export function browseUrl(s: BrowseState): string { return `/api/admin/brain/entities?${browseQuery(s)}`; }

/** `#/brain/type/<T>` — a bookmarkable type filter. */
export function typeFromHash(hash: string): string {
  const m = /^#\/brain\/type\/(.+)$/.exec(hash);
  return m ? decodeURIComponent(m[1] as string) : '';
}

/** "rows 1-50 of 123" — or the named nothing. */
export function pageLabel(r: Pick<BrowseResponse, 'offset' | 'total' | 'rows'>): string {
  if (!r.rows.length) return `rows 0 of ${r.total}`;
  return `rows ${r.offset + 1}-${r.offset + r.rows.length} of ${r.total}`;
}

/** The state line: the server's measured line, verbatim, for both states. */
export function stateLine(r: BrowseResponse): { text: string; cls: string } {
  return r.state === 'empty'
    ? { text: `${r.line} (state: empty${r.reason ? ', ' + r.reason : ''})`, cls: 'hc-brain-empty' }
    : { text: r.line, cls: 'hc-brain-line' };
}

/** A refusal, verbatim: the status and the server's own error string. */
export function refusalLine(status: number, data: unknown): string {
  const err = data && typeof data === 'object' && 'error' in data ? String((data as { error: unknown }).error) : '';
  return `entities: ${status}${err ? ' ' + err : status === 0 ? ' (network)' : ''}`;
}

export function tagOptions(tags: BrowseResponse['tags']): FcOption[] {
  return [{ value: '', label: 'All tags' }, ...tags.map((t) => ({ value: t.tag, label: `${t.tag} (${t.count})` }))];
}

/** The type list: "All" plus every type the schema defines (0-count types
 *  included, marked) and any type an entity carries that the schema does
 *  not. Each item is a link; a click filters in place. */
export function renderTypeList(host: HTMLElement, types: BrowseType[], all: number, active: string, onPick: (type: string) => void): void {
  host.innerHTML = '';
  const ul = el('ul', 'hc-brain-types');
  const item = (type: string, label: string, count: number, extra: string) => {
    const li = el('li', `hc-brain-type${type === active ? ' hc-brain-type-on' : ''}${count === 0 ? ' hc-brain-type-zero' : ''}${extra}`);
    const a = el('a', 'hc-brain-type-a', label);
    a.href = typeHref(type);
    a.setAttribute('data-type', type);
    if (type === active) a.setAttribute('aria-current', 'true');
    a.addEventListener('click', (ev: Event) => { ev.preventDefault(); onPick(type); });
    li.appendChild(a);
    li.appendChild(el('span', 'hc-brain-type-n', String(count)));
    ul.appendChild(li);
  };
  item('', 'All types', all, '');
  for (const t of types) item(t.type, t.type, t.count, t.in_schema ? '' : ' hc-brain-type-unschema');
  host.appendChild(ul);
}

export function buildBrowseColumns(): ColDef<BrowseRow>[] {
  return [
    { colId: 'id', headerName: 'Id', width: 170, cellClass: 'hc-mono', valueGetter: (p) => p.data?.id ?? '', cellRenderer: (p: { data?: BrowseRow }) => (p.data ? entityLink(p.data.id) : '') },
    { colId: 'name', headerName: 'Name', flex: 1, minWidth: 180, valueGetter: (p) => p.data?.name ?? '—' },
    { colId: 'type', headerName: 'Type', width: 150, valueGetter: (p) => !p.data ? '' : `${p.data.type}${p.data.proposed_type ? ` (proposed ${p.data.proposed_type})` : ''}` },
    { colId: 'tags', headerName: 'Tags', width: 180, valueGetter: (p) => (p.data?.tags ?? []).join(', ') },
    { colId: 'claims', headerName: 'Claims', width: 96, valueGetter: (p) => p.data?.claims ?? 0 },
    { colId: 'relations', headerName: 'Relations', width: 110, valueGetter: (p) => p.data?.relations ?? 0 },
    { colId: 'invalidated', headerName: 'Invalidated', width: 120, valueGetter: (p) => p.data?.invalidated ?? 0 },
    { colId: 'changed', headerName: 'Last changed', width: 190, valueGetter: (p) => p.data?.last_changed ?? '', cellRenderer: (p: { data?: BrowseRow }) => !p.data?.last_changed ? '—' : `${relativeTime(p.data.last_changed)} · ${(p.data.last_commit ?? '').slice(0, 12)}` },
  ];
}

export const BROWSE_COLUMN_IDS = ['id', 'name', 'type', 'tags', 'claims', 'relations', 'invalidated', 'changed'];

/** Mount the pane into `host`. `initialType` comes from `#/brain/type/<T>`. */
export function mountBrowse(host: HTMLElement, initialType = ''): () => void {
  const st: BrowseState = { type: initialType, tag: '', q: '', offset: 0, limit: BROWSE_PAGE };
  let disposed = false;
  let grid: GridApi<BrowseRow> | null = null;
  let debounce: number | null = null;
  let tags: BrowseResponse['tags'] = [];
  const bar = el('div', 'hc-brain-browsebar');
  const search = mkSearchBox({
    id: 'hcBrainQ', label: 'Search the brain', placeholder: 'Search id, name, alias or claim value',
    onInput: () => { if (debounce) window.clearTimeout(debounce); debounce = window.setTimeout(() => { st.q = search.value(); st.offset = 0; void load(); }, 250); },
  });
  const tagSel: FcSelectCtl = mkFcSelect({ id: 'hcBrainTag', label: 'Filter by tag', cls: 'hc-select', options: () => tagOptions(tags), value: () => st.tag, onPick: (v) => { st.tag = v; st.offset = 0; void load(); } });
  const prev = mkIconBtn({ glyph: 'arrow-left', label: 'Previous page', size: 'sm' });
  const next = mkIconBtn({ glyph: 'arrow-right', label: 'Next page', size: 'sm' });
  const pageEl = el('span', 'hc-brain-page');
  prev.addEventListener('click', () => { st.offset = Math.max(0, st.offset - st.limit); void load(); });
  next.addEventListener('click', () => { st.offset += st.limit; void load(); });
  bar.append(search.el, tagSel.el, prev, pageEl, next);
  const layout = el('div', 'hc-brain-browse');
  const typesEl = el('nav', 'hc-brain-typelist');
  typesEl.setAttribute('aria-label', 'Entity types');
  const right = el('div', 'hc-brain-browse-main');
  const note = el('p', 'hc-brain-line');
  const gridHost = el('div', 'hc-grid hc-brain-grid');
  right.append(bar, note, gridHost);
  layout.append(typesEl, right);
  host.appendChild(layout);

  async function load(): Promise<void> {
    const r = await jsonFetch<BrowseResponse>('GET', `/api/admin/brain/entities?${browseQuery(st)}`);
    if (disposed) return;
    if (r.status !== 200 || !r.data) {
      note.className = 'hc-brain-refusal'; note.textContent = refusalLine(r.status, r.data);
      grid?.setGridOption('rowData', []); typesEl.innerHTML = ''; pageEl.textContent = '';
      return;
    }
    const d = r.data;
    tags = d.tags; tagSel.set();
    const s = stateLine(d); note.className = s.cls; note.textContent = s.text;
    renderTypeList(typesEl, d.types, d.all, st.type, (t) => { st.type = t; st.offset = 0; void load(); });
    pageEl.textContent = pageLabel(d);
    prev.disabled = d.offset <= 0;
    next.disabled = d.offset + d.rows.length >= d.total;
    if (!grid) grid = makeGrid<BrowseRow>(gridHost, { columnDefs: buildBrowseColumns(), rowData: d.rows });
    else grid.setGridOption('rowData', d.rows);
  }
  void load();
  return () => { disposed = true; if (debounce) window.clearTimeout(debounce); grid?.destroy(); grid = null; };
}
